'use client';

import Uppy from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import ImageEditor from '@uppy/image-editor';
import Transloadit from '@uppy/transloadit';
import Tus from '@uppy/tus';
import { useEffect, useRef } from 'react';

import '@uppy/core/dist/style.css';
import '@uppy/dashboard/dist/style.css';
import '@uppy/image-editor/dist/style.css';
import { logger } from '@/utils/logger';

interface MediaUploaderProps {
  entityId: string;
  showUploader: boolean;
  onImageUpdate: (url: string[]) => void;
  allowMultiple?: boolean;
}

export function MediaUploader({
  entityId,
  showUploader,
  onImageUpdate,
  allowMultiple = false,
}: MediaUploaderProps) {
  const uppyRef = useRef<Uppy | null>(null);
  const dashboardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Only initialize Uppy when showUploader is true and the ref is available
    if (!showUploader || !dashboardRef.current) return;

    logger.debug(`MediaUploader: Initializing for entity ${entityId}`);

    const newUppy = new Uppy({
      id: 'mediaUploader',
      autoProceed: false,
      restrictions: {
        maxNumberOfFiles: allowMultiple ? 5 : 1,
        allowedFileTypes: ['.jpg', '.jpeg', '.png', '.avif', '.heic'],
        maxFileSize: 10 * 1024 * 1024, // 10MB in bytes
      },
      logger: logger,
    })
      .use(Dashboard, {
        inline: true,
        target: dashboardRef.current,
        showProgressDetails: true,
        proudlyDisplayPoweredByUppy: false,
        height: 300,
        width: '100%',
        autoOpen: 'imageEditor',
      })
      .use(ImageEditor, {
        quality: 0.8,
        cropperOptions: {
          zoomable: true,
          modal: true,
          background: true,
          guides: true,
          center: true,
          responsive: true,
          autoCrop: true,
          autoCropArea: 1,
          movable: true,
          zoomOnTouch: true,
          zoomOnWheel: true,
        },
      });

    if (process.env.NODE_ENV === 'development') {
      newUppy.use(Tus, {
        endpoint: process.env.NEXT_PUBLIC_TUS_ENDPOINT!,
      });
    } else {
      // Use Transloadit for production
      newUppy.use(Transloadit, {
        waitForEncoding: true,
        assemblyOptions: {
          params: {
            auth: { key: process.env.NEXT_PUBLIC_TRANSLOADIT_AUTH_KEY! },
            template_id: process.env.NEXT_PUBLIC_TRANSLOADIT_TEMPLATE_ID!,
            fields: {
              user_id: entityId,
            },
          },
        },
      });
    }

    newUppy.on('file-added', (file) => {
      logger.debug(
        `MediaUploader: File added - ${file.name}, size: ${file.size}, type: ${file.type}`,
      );
    });

    newUppy.on('transloadit:assembly-error', (assembly) => {
      logger.error(
        `MediaUploader: Assembly error ${assembly.error || assembly}`,
      );
    });

    // Only register complete event in development
    if (process.env.NODE_ENV === 'development') {
      newUppy.on('complete', (result) => {
        logger.debug(
          `MediaUploader: Upload complete in dev. Successful: ${result.successful?.length}, Failed: ${result.failed?.length}`,
        );

        if (result.successful && result.successful.length > 0) {
          const urls = result.successful
            .filter((file) => file.uploadURL)
            .map((file) => file.uploadURL as string);

          if (urls.length > 0) {
            onImageUpdate(urls);
          }
        }
      });
    } else {
      newUppy.on('transloadit:complete', (assembly) => {
        logger.debug(
          `MediaUploader: Assembly results: ${JSON.stringify(assembly.results)}`,
        );

        if (assembly.results?.resized) {
          const urls = assembly.results.resized
            .filter((file) => file.ssl_url)
            .map((file) => file.ssl_url);

          if (urls.length > 0) {
            onImageUpdate(urls);
          }
        }
      });
    }

    uppyRef.current = newUppy;

    // Cleanup function
    return () => {
      if (uppyRef.current) {
        uppyRef.current.destroy();
        uppyRef.current = null;
      }
    };
  }, [showUploader, entityId, onImageUpdate, allowMultiple]);

  return (
    <div
      ref={dashboardRef}
      style={{ marginTop: 15, marginBottom: 15 }}
    />
  );
}
